import { useEffect, useState } from 'react'
import API_BASE_URL from '../ScrapFlow/config/config.server'
import RealMap, { RouteMetrics } from './components/RealMap'
import { getLocationHealthState } from './utils/trackingState'

/**
 * Unauthenticated "share this trip" page — reached via a plain /track/:token
 * URL (no login), hitting the existing public GET /api/transport/track/:token
 * endpoint. Deliberately uses raw fetch, not useAuth().apiRequest, since
 * there's no session here at all.
 */

const POLL_MS = 6000

interface Tracking {
  bookingCode: string
  status: string
  vehicleName: string
  driverName: string | null
  driverRating: number | null
  vehicleNumber: string | null
  pickupAddress: string
  dropoffAddress: string
  pickup: { lat: number; lng: number }
  dropoff: { lat: number; lng: number }
  driverPosition: { lat: number; lng: number } | null
  driverLastLocationAt: string | null
  stops: { stopId: number; sequenceOrder: number; lat: number; lng: number; address: string; visitedAt: string | null }[]
  deliveryPin: string | null
}

const STATUS_LABEL: Record<string, string> = {
  scheduled: 'Scheduled',
  searching: 'Finding a driver…',
  assigned: 'Driver assigned',
  arriving: 'Driver is arriving',
  arrived_pickup: 'Driver has arrived for pickup',
  picked_up: 'Material picked up',
  en_route: 'On the way to destination',
  arrived_dropoff: 'Arrived at destination',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
}

export default function PublicTrackingPage({ token }: { token: string }) {
  const [tracking, setTracking] = useState<Tracking | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [routeMetrics, setRouteMetrics] = useState<RouteMetrics | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = () => {
      fetch(`${API_BASE_URL}/api/transport/track/${encodeURIComponent(token)}`)
        .then(res => res.json())
        .then(data => {
          if (cancelled) return
          if (data?.success === false) {
            setError(data.message || 'This tracking link is invalid or has expired.')
          } else {
            setTracking(data.tracking)
          }
        })
        .catch(() => { if (!cancelled) setError('Unable to load tracking info. Check your connection.') })
        .finally(() => { if (!cancelled) setLoading(false) })
    }
    load()
    const iv = setInterval(load, POLL_MS)
    return () => { cancelled = true; clearInterval(iv) }
  }, [token])

  const finished = tracking?.status === 'delivered' || tracking?.status === 'cancelled'
  const activeStop = tracking?.stops.find(stop => !stop.visitedAt) ?? null
  const activeDestination = activeStop || tracking?.dropoff || null
  const lastUpdatedSeconds = tracking?.driverLastLocationAt
    ? Math.max(0, Math.floor((Date.now() - new Date(tracking.driverLastLocationAt).getTime()) / 1000))
    : null
  const locationHealth = getLocationHealthState({ lastUpdatedSeconds, status: tracking?.status ?? null })
  const locationStatusLabel = locationHealth === 'live' ? 'Live' : locationHealth === 'delayed' ? 'Delayed' : locationHealth === 'stale' ? 'Temporarily unavailable' : 'Tracking ended'

  return (
    <div style={{ minHeight: '100vh', background: '#0C0C0C', color: '#fff', fontFamily: 'system-ui, sans-serif', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '32px 16px' }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
          <span style={{ fontSize: 22 }}>🚚</span>
          <span style={{ fontSize: 15, fontWeight: 700 }}>Afdani Transport · Live Tracking</span>
        </div>

        {loading && <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14 }}>Loading…</div>}

        {!loading && error && (
          <div style={{ border: '1px solid rgba(255,69,58,0.3)', background: 'rgba(255,69,58,0.08)', borderRadius: 16, padding: 16, color: '#FF453A', fontSize: 13.5 }}>
            {error}
          </div>
        )}

        {!loading && tracking && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ background: '#141416', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 18, padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ width: 8, height: 8, borderRadius: 999, background: finished ? (tracking.status === 'delivered' ? '#00D46A' : '#FF453A') : locationHealth === 'live' ? '#00D46A' : locationHealth === 'delayed' ? '#FFB340' : locationHealth === 'stale' ? '#FF453A' : '#8E8E93' }} />
                <span style={{ fontSize: 14.5, fontWeight: 600 }}>{STATUS_LABEL[tracking.status] || tracking.status}</span>
              </div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>Booking {tracking.bookingCode} · {tracking.vehicleName}</div>
            </div>

            <div style={{ background: '#141416', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 18, padding: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)' }}>Location status</div>
              <div style={{ fontSize: 11, color: locationHealth === 'live' ? '#00D46A' : locationHealth === 'delayed' ? '#FFB340' : locationHealth === 'stale' ? '#FF453A' : '#8E8E93', fontWeight: 700 }}>{locationStatusLabel}</div>
            </div>

            {tracking.pickup && activeDestination && (
              <div style={{ height: 220, overflow: 'hidden', borderRadius: 18, border: '1px solid rgba(255,255,255,0.08)' }}>
                <RealMap
                  className="h-full"
                  pickup={tracking.pickup}
                  stops={tracking.stops}
                  dropoff={tracking.dropoff}
                  driverPosition={tracking.driverPosition}
                  routeFrom={tracking.driverPosition}
                  routeTo={activeDestination}
                  onRouteMetrics={setRouteMetrics}
                />
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, background: '#141416', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 18, padding: 16 }}>
              <div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Current destination</div>
                <div style={{ fontSize: 13 }}>{activeStop?.address || tracking.dropoffAddress}</div>
              </div>
              <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#00D46A' }}>{routeMetrics ? `${Math.max(1, Math.ceil(routeMetrics.durationSeconds / 60))} min` : 'Calculating…'}</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>{routeMetrics ? `${(routeMetrics.distanceMeters / 1000).toFixed(1)} km` : 'Route ETA'}</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 3 }}>
                  {lastUpdatedSeconds == null
                    ? 'Location unavailable'
                    : locationHealth === 'live'
                      ? `Location updated ${lastUpdatedSeconds}s ago`
                      : locationHealth === 'delayed'
                        ? `Driver delayed by ${lastUpdatedSeconds}s`
                        : locationHealth === 'stale'
                          ? 'Driver location is stale right now'
                          : 'Tracking has ended'}
                </div>
              </div>
            </div>

            {tracking.driverName && (
              <div style={{ background: '#141416', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 18, padding: 16 }}>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Driver</div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{tracking.driverName}</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>
                  {tracking.driverRating != null ? `★ ${tracking.driverRating.toFixed(1)}` : ''}
                  {tracking.vehicleNumber ? ` · ${tracking.vehicleNumber}` : ''}
                </div>
              </div>
            )}

            <div style={{ background: '#141416', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 18, padding: 16 }}>
              <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                <span style={{ color: '#00D46A' }}>●</span>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)' }}>{tracking.pickupAddress}</div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <span style={{ color: '#FF453A' }}>●</span>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)' }}>{tracking.dropoffAddress}</div>
              </div>
            </div>

            {tracking.deliveryPin && (
              <div style={{ background: 'rgba(0,212,106,0.08)', border: '1px solid rgba(0,212,106,0.25)', borderRadius: 18, padding: 16, textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>Delivery PIN — share only with the driver</div>
                <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: 4 }}>{tracking.deliveryPin}</div>
              </div>
            )}

            {!finished && <div style={{ textAlign: 'center', fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>Updates automatically</div>}
          </div>
        )}
      </div>
    </div>
  )
}
